import React, { useState, useEffect } from "react";
import VisibilitySensor from "react-visibility-sensor";
import styles from "./index.module.scss";
import Tooltip from "@material-ui/core/Tooltip";
import * as icons from "../../config/Icons";
import { useTranslation } from "react-i18next";

export default function BarChartStackedMulti({
  title,
  data,
  colors,
  fontColors,
  legends,
  labelX,
  labelY,
  showTotal,
  totalSuffix,
  animDuration = 1000,
  animPredelay = 0,
  ...props
}) {
  const { t } = useTranslation();
  const [totals, setTotals] = useState([]);
  const [maxTotal, setMaxTotal] = useState([]);
  const [countryAverage, setCountryAverage] = useState([]);
  const [regionDetails, setRegionDetails] = useState([]);
  const [showValues, setShowValues] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (data && data.country_average) {
      const currentData = data.country_average;
      let newObj = [];
      if (currentData.length > 0) {
        Object.keys(currentData[0]).map((session, index) => {
          let obj = {
            label: session,
            values: [],
            maxTotal: 0,
          };
          currentData[0][session].map(item => {
            obj.values.push(item.average);
            obj.maxTotal = obj.maxTotal + item.average;
          });
          newObj.push(obj);
        });
        setCountryAverage(newObj);
      }
    }

    if (data && data.region_details) {
      const regionData = data.region_details;
      let formattedRegionData = [];
      if (regionData.length > 0) {
        Object.keys(regionData[0]).map((item, index) => {
          let obj = {
            title: regionData[0][item][0].title,
            label: "Average",
            values: [],
            maxTotal: 0,
          };
          regionData[0][item].map(country => {
            obj.values.push(country.average);
            obj.maxTotal = obj.maxTotal + country.average;
          });
          formattedRegionData.push(obj);
        });
      }

      setRegionDetails(formattedRegionData);
    }

    // updateChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setShowValues(true);
      }, animPredelay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  useEffect(() => {
    if (countryAverage.length > 0) {
      updateChart();
    }
  }, [countryAverage]);

  // only animate on component visibility
  const onVisibilityChange = isVisible => {
    if (isVisible) {
      setIsVisible(true);
    }
  };

  const updateChart = () => {
    let maxTotal = -Infinity;
    let totals = [];
    countryAverage.forEach(item => {
      let itemTotal = 0;
      item.values.forEach(v => {
        itemTotal += v;
      });
      totals.push(itemTotal);
      if (itemTotal > maxTotal) {
        maxTotal = itemTotal;
      }
    });
    setMaxTotal(maxTotal);
    setTotals(totals);
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <strong>{title}</strong>
      </div>
      {data.country_average && data.country_average.length > 0 ? (
        <>
          <div className={styles.barCharts}>
            <VisibilitySensor onChange={onVisibilityChange}>
              <ul className={styles.barChartsList}>
                <li className={styles.barChartsHeader}>
                  <div className={styles.labelLeft}> Country </div>
                  <div className={styles.labelX}>{labelX}</div>
                  <div className={[styles.labelY, styles.show].join(" ")}>
                    {labelY}
                  </div>
                </li>
                {countryAverage &&
                  countryAverage.map((item, index) => {
                    return (
                      <li className={styles.barChartsItem} key={index}>
                        <div className={styles.labelLeft}>
                          {index === 1 && "Country Average"}
                        </div>
                        <div className={styles.labelX}>{item.label}</div>
                        <div className={[styles.labelY].join(" ")}>
                          <Tooltip
                            title={
                              <TooltipContent
                                data={item}
                                title="Country (Avg)"
                              />
                            }
                            placement="bottom-start"
                            PopperProps={{
                              popperOptions: {
                                modifiers: {
                                  offset: {
                                    enabled: true,
                                    offset: "0, -20px",
                                  },
                                },
                              },
                            }}
                          >
                            <div
                              style={{ transition: `${animDuration}ms width` }}
                              className={[
                                styles.labelYInner,
                                showValues ? styles.show : "",
                              ].join(" ")}
                            >
                              {item.values.map((value, _index) => {
                                let barWidth = (value / maxTotal) * 100;
                                return (
                                  <div
                                    key={_index}
                                    className={styles.bar}
                                    style={{
                                      width: `${barWidth}%`,
                                      backgroundColor: `${colors[_index]}`,
                                    }}
                                  >
                                    <span
                                      style={{
                                        transitionDuration: `${animDuration}ms`,
                                        color: `${fontColors[_index]}`,
                                      }}
                                    >
                                      {value}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </Tooltip>
                        </div>
                        {showTotal && (
                          <div className={styles.total}>
                            {t("dashboard.coach.total")}
                            <strong>
                              {`${totals[index]} `}
                              {totalSuffix}
                            </strong>
                          </div>
                        )}
                      </li>
                    );
                  })}
                <li className={styles.barChartsItem} style={{ height: "30px" }}>
                  <div className={styles.labelLeft}></div>
                  <div
                    className={styles.labelX}
                    style={{ height: "100%" }}
                  ></div>
                </li>
                {regionDetails &&
                  regionDetails.map((item, index) => {
                    const flagIcon =
                      item && item.title && item.title.trim() + "FlagIcon";
                    return (
                      <li className={styles.barChartsItem} key={index}>
                        <div className={styles.labelLeft}>
                          <img src={icons[flagIcon]} alt={item.name} />{" "}
                          {item.title}
                        </div>
                        <div className={styles.labelX}>{item.label}</div>
                        <div className={[styles.labelY].join(" ")}>
                          <Tooltip
                            title={
                              <TooltipContent data={item} title={item.title} />
                            }
                            placement="bottom-start"
                            PopperProps={{
                              popperOptions: {
                                modifiers: {
                                  offset: {
                                    enabled: true,
                                    offset: "0, -10px",
                                  },
                                },
                              },
                            }}
                          >
                            <div
                              style={{ transition: `${animDuration}ms width` }}
                              className={[
                                styles.labelYInner,
                                showValues ? styles.show : "",
                              ].join(" ")}
                            >
                              {item.values.map((value, _index) => {
                                let barWidth = (value / maxTotal) * 100;
                                return (
                                  <div
                                    key={_index}
                                    className={styles.bar}
                                    style={{
                                      width: `${barWidth}%`,
                                      backgroundColor: `${colors[_index]}`,
                                    }}
                                  >
                                    <span
                                      style={{
                                        transitionDuration: `${animDuration}ms`,
                                        color: `${fontColors[_index]}`,
                                      }}
                                    >
                                      {value}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </Tooltip>
                        </div>
                        {showTotal && (
                          <div className={styles.total}>
                            {t("dashboard.coach.total")}
                            <strong>
                              {`${totals[index]} `}
                              {totalSuffix}
                            </strong>
                          </div>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </VisibilitySensor>
          </div>
          <div className={styles.footer}>
            <div className={styles.legends}>
              <ul className={styles.legendsList}>
                {legends.map((legend, index) => {
                  return (
                    <li className={styles.legendItem} key={index}>
                      <div
                        className={styles.legendColor}
                        style={{ backgroundColor: `${colors[index]}` }}
                      ></div>
                      <span>{legend}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.noData}>{t("dashboard.noDataYet")}</div>
      )}
    </div>
  );
}

function TooltipContent({ data, title, ...props }) {
  const { t } = useTranslation();
  return (
    <div className={styles.tooltipContainer}>
      <span className={styles.bold}>{title} </span>
      <span className={styles.bold}>{data.label} </span>
      <span>
        {" "}
        {t("dashboard.coach.plan")} : <span>{data.values[0]} mins</span>
      </span>
      <span>
        {" "}
        {t("dashboard.coach.do")} : <span>{data.values[1]} mins</span>
      </span>
      <span>
        {" "}
        {t("dashboard.coach.check")} : <span>{data.values[2]} mins</span>
      </span>
      <span>
        {" "}
        {t("dashboard.coach.act")} : <span>{data.values[3]} mins</span>
      </span>
      <span className={styles.total}>
        {" "}
        {t("dashboard.coach.total")}: <span>{data.maxTotal} mins</span>
      </span>
    </div>
  );
}
